import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';

const ChromaticVignetteShader = {
  uniforms: {
    tDiffuse: { value: null },
    chromaticAmount: { value: 0.0 },
    chromaticAngle: { value: 0.0 },
    vignetteDarkness: { value: 0.0 },
    vignetteOffset: { value: 1.0 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float chromaticAmount;
    uniform float chromaticAngle;
    uniform float vignetteDarkness;
    uniform float vignetteOffset;
    varying vec2 vUv;
    void main() {
      vec2 offset = chromaticAmount * vec2(cos(chromaticAngle), sin(chromaticAngle));
      float r = texture2D(tDiffuse, vUv + offset).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv - offset).b;
      vec3 color = vec3(r, g, b);
      vec2 uv = (vUv - vec2(0.5)) * vec2(vignetteOffset);
      float vigDist = length(uv);
      float vig = smoothstep(0.8, 0.4, vigDist);
      color = mix(color, color * vig, vignetteDarkness);
      gl_FragColor = vec4(color, 1.0);
    }
  `,
};

export class PostProcessing {
  private composer: EffectComposer;
  private bloomPass: UnrealBloomPass;
  private outlinePass: OutlinePass | null = null;
  private chromaticVignettePass: ShaderPass;

  // Half-resolution bloom saves ~75% fillrate
  private static readonly BLOOM_RESOLUTION_DIVISOR = 2;

  // Transient effect state
  private chromaticIntensity: number = 0;
  private vignetteIntensity: number = 0;
  private baseVignette: number = 0;

  // Store references for outline pass insertion
  private scene: THREE.Scene;
  private camera: THREE.Camera;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    this.scene = scene;
    this.camera = camera;
    this.composer = new EffectComposer(renderer);

    const renderPass = new RenderPass(scene, camera);
    this.composer.addPass(renderPass);

    // Bloom for spell glow - half-resolution for performance
    const size = renderer.getSize(new THREE.Vector2());
    const bloomRes = size.clone().divideScalar(PostProcessing.BLOOM_RESOLUTION_DIVISOR).floor();
    this.bloomPass = new UnrealBloomPass(
      bloomRes,
      0.6,  // strength
      0.4,  // radius
      0.85  // threshold
    );
    this.composer.addPass(this.bloomPass);

    // Outline pass slot — initialized lazily via initOutlines()
    // Pass chain: Render → Bloom → [Outline] → ChromaticVignette → Output

    // Merged chromatic aberration + vignette (saves one full-screen pass)
    this.chromaticVignettePass = new ShaderPass(ChromaticVignetteShader);
    this.chromaticVignettePass.uniforms['chromaticAmount'].value = 0;
    this.chromaticVignettePass.uniforms['vignetteDarkness'].value = 0;
    this.chromaticVignettePass.uniforms['vignetteOffset'].value = 1.0;
    this.composer.addPass(this.chromaticVignettePass);

    // Output pass for correct color space
    const outputPass = new OutputPass();
    this.composer.addPass(outputPass);
  }

  /**
   * Initialize toon outline post-processing.
   * Inserts an OutlinePass after bloom in the chain.
   * Call after wizard models are created.
   */
  initOutlines(selectedObjects: THREE.Object3D[]): void {
    if (this.outlinePass) return; // Already initialized

    const size = this.composer.renderer.getSize(new THREE.Vector2());
    this.outlinePass = new OutlinePass(
      new THREE.Vector2(size.x, size.y),
      this.scene,
      this.camera
    );

    // Toon outline configuration: dark edges, no glow
    this.outlinePass.edgeStrength = 3.0;
    this.outlinePass.edgeThickness = 1.5;
    this.outlinePass.visibleEdgeColor.set(0x111111);
    this.outlinePass.hiddenEdgeColor.set(0x111111);
    this.outlinePass.edgeGlow = 0;
    this.outlinePass.pulsePeriod = 0; // No pulsing

    this.outlinePass.selectedObjects = selectedObjects;

    // Insert after bloom pass (index 1) and before chromaticVignette
    // Passes: [0]=Render, [1]=Bloom, [2]=ChromaticVignette, [3]=Output
    // Insert at index 2, pushing ChromaticVignette and Output to 3 and 4
    this.composer.insertPass(this.outlinePass, 2);
  }

  /** Update the objects that receive toon outlines */
  setOutlineObjects(objects: THREE.Object3D[]): void {
    if (this.outlinePass) {
      this.outlinePass.selectedObjects = objects;
    }
  }

  /** Call instead of renderer.render() */
  render(): void {
    this.composer.render();
  }

  /** Call every frame to decay transient effects */
  update(_delta: number): void {
    // Decay chromatic aberration
    if (this.chromaticIntensity > 0) {
      this.chromaticIntensity *= 0.92;
      if (this.chromaticIntensity < 0.0005) {
        this.chromaticIntensity = 0;
      }
      this.chromaticVignettePass.uniforms['chromaticAmount'].value = this.chromaticIntensity;
      this.chromaticVignettePass.uniforms['chromaticAngle'].value += 0.1;
    }

    // Update vignette (combines base + transient)
    if (this.vignetteIntensity > 0) {
      this.vignetteIntensity *= 0.95;
      if (this.vignetteIntensity < 0.01) {
        this.vignetteIntensity = 0;
      }
    }
    this.chromaticVignettePass.uniforms['vignetteDarkness'].value = this.baseVignette + this.vignetteIntensity;
  }

  /** Trigger chromatic aberration on spell impact */
  triggerImpactEffect(intensity: number = 0.008): void {
    this.chromaticIntensity = Math.min(intensity, 0.02);
  }

  /** Set persistent vignette based on health ratio (0 = full health, 1 = near death) */
  setHealthVignette(damageRatio: number): void {
    // damageRatio: 0 = full health, 1 = dead
    this.baseVignette = Math.max(0, damageRatio - 0.3) * 1.2;
  }

  /** Trigger a brief vignette flash (e.g. on taking damage) */
  triggerDamageFlash(intensity: number = 0.5): void {
    this.vignetteIntensity = Math.min(intensity, 1.0);
  }

  /** Temporarily boost bloom for dramatic moments */
  setBloomStrength(strength: number): void {
    this.bloomPass.strength = strength;
  }

  resetBloom(): void {
    this.bloomPass.strength = 0.6;
  }

  onResize(width: number, height: number): void {
    const d = PostProcessing.BLOOM_RESOLUTION_DIVISOR;
    this.bloomPass.resolution.set(Math.floor(width / d), Math.floor(height / d));
    if (this.outlinePass) {
      this.outlinePass.resolution.set(width, height);
    }
    this.composer.setSize(width, height);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
